import { Button } from '~/primitives/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/primitives/dialog';
import { RadioGroup, RadioGroupItem } from '~/primitives/radiogroup';
import { classicTags, platTags } from '~/util/types/tags';
import { ScrollArea } from '~/primitives/scroll-area';
import { NlwTier, nlwTiers } from '~/util/types/nlw';
import { Checkbox } from '~/primitives/checkbox';
import { ArrowDownAZ, ArrowUpZA } from 'lucide-react';
import { getRouteApi } from '@tanstack/react-router';
import {
    defaultListParams,
    rangeProperties,
    rangeProperty,
} from './-SearchParams';
import ListTagsSelector from '~/components/ListTagsSelector';
import { useResettableState } from '~/util/hooks/useResettableState';
import { NumberInput } from '~/primitives/input';

const Route = getRouteApi('/list');

interface ListFilterProps {
    isOpen: boolean;
    onClose: () => void;
}

const propertyToLabelMap: Record<string, string> = {
    rank: 'Position',
    edel_enjoyment: 'EDEL Enjoyment',
    gddl_tier: 'GDDL Tier',
};

const ListFiltersModal: React.FC<ListFilterProps> = ({ isOpen, onClose }) => {
    const searchParams = Route.useSearch();
    const navigate = Route.useNavigate();

    const [localParams, setLocalParams] = useResettableState(searchParams);
    const selectedTiers = new Set(localParams.filters.tiers);

    const handleReverseChange = () => {
        setLocalParams((prev) => ({
            ...prev,
            reverse: !prev.reverse,
        }));
    };

    const handleTiersModeChange = (value: boolean) => {
        setLocalParams((prev) => ({
            ...prev,
            tiers_mode: value,
        }));
    };

    const handleSortChange = (
        sort: 'rank' | 'enjoyment' | 'records' | 'creation_date',
    ) => {
        setLocalParams((prev) => ({
            ...prev,
            sort,
        }));
    };

    const handleTierChange = (tier: NlwTier) => {
        setLocalParams((prev) => {
            const tiers = prev.filters.tiers.includes(tier)
                ? prev.filters.tiers.filter((t) => t !== tier)
                : [...prev.filters.tiers, tier];
            return { ...prev, filters: { ...prev.filters, tiers } };
        });
    };

    const handleFavoriteChange = (value: boolean) => {
        setLocalParams((prev) => ({
            ...prev,
            filters: {
                ...prev.filters,
                favorited_by_user: value,
            },
        }));
    };

    const handleRangeChange = (
        property: rangeProperty,
        key: 'min' | 'max',
        value: number | undefined | null,
    ) => {
        setLocalParams((prev) => ({
            ...prev,
            filters: {
                ...prev.filters,
                ranges: {
                    ...prev.filters.ranges,
                    [property]: {
                        ...prev.filters.ranges[property],
                        [key]: value ?? undefined,
                    },
                },
            },
        }));
    };

    const handleIncludedLevelsChange = (
        value: 'all' | 'completed' | 'uncompleted',
    ) => {
        setLocalParams((prev) => ({
            ...prev,
            filters: {
                ...prev.filters,
                completed_by_user: value,
            },
        }));
    };

    const applyFilters = () => {
        navigate({
            from: '/list/$levelid',
            search: localParams,
            replace: true,
        });
        onClose();
    };

    const resetFilters = () => {
        setLocalParams({
            ...defaultListParams,
            list: searchParams.list,
        });
        navigate({
            from: '/list/$levelid',
            search: defaultListParams,
            replace: true,
        });
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>List Search Settings</DialogTitle>
                    <DialogDescription>
                        Sort and filter options for levels shown on the list.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 md:grid-cols-6">
                    <div className="md:col-span-2">
                        <h3
                            id="sort-label"
                            className="clamp-[text-lg-xl-clamp] font-semibold mb-2"
                        >
                            Sort Type{' '}
                            {localParams.reverse ? (
                                <ArrowUpZA
                                    className="inline text-secondary-light hover:text-secondary-foreground cursor-pointer transition-colors"
                                    onClick={handleReverseChange}
                                />
                            ) : (
                                <ArrowDownAZ
                                    className="inline text-secondary-light hover:text-secondary-foreground cursor-pointer transition-colors"
                                    onClick={handleReverseChange}
                                />
                            )}
                        </h3>
                        <div className="flex flex-col justify-between mb-4 bg-background-dark p-4 rounded-xl lg:h-[30vh]">
                            <RadioGroup
                                value={localParams.sort}
                                onValueChange={handleSortChange}
                                aria-labelledby="sort-label"
                            >
                                <RadioGroupItem
                                    value="rank"
                                    id="rank"
                                    className="clamp-[text-sm-lg-clamp]"
                                    label="AREDL Rank"
                                />
                                <RadioGroupItem
                                    value="enjoyment"
                                    id="enjoyment"
                                    label="EDEL Enjoyment"
                                />
                                <RadioGroupItem
                                    value="records"
                                    id="records"
                                    label="Records count"
                                />
                                <RadioGroupItem
                                    value="creation_date"
                                    id="creation_date"
                                    label="Creation Date"
                                />
                            </RadioGroup>
                            <div className="flex items-center gap-2 px-1">
                                <Checkbox
                                    id="tiers_mode"
                                    checked={localParams.tiers_mode}
                                    onCheckedChange={(value) =>
                                        handleTiersModeChange(!!value)
                                    }
                                />
                                <label
                                    htmlFor="tiers_mode"
                                    className="clamp-[text-sm-lg-clamp]"
                                >
                                    Group by NLW tiers
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <h3 className="clamp-[text-lg-xl-clamp] font-semibold mb-2 ">
                            Filters
                        </h3>

                        <div className="mb-4 bg-background-dark p-2 rounded-xl lg:h-[30vh]">
                            <ScrollArea className="h-full">
                                <div className="mb-4 p-2">
                                    <h3 className="clamp-[text-sm-lg-clamp] font-semibold">
                                        Completion status:
                                    </h3>
                                    <RadioGroup
                                        value={
                                            localParams.filters
                                                .completed_by_user
                                        }
                                        onValueChange={
                                            handleIncludedLevelsChange
                                        }
                                        aria-labelledby="levels-completed-by-label"
                                        className="mb-2"
                                    >
                                        <RadioGroupItem
                                            value="all"
                                            id="all"
                                            className="clamp-[text-sm-lg-clamp]"
                                            label="All Levels"
                                        />
                                        <RadioGroupItem
                                            value="completed"
                                            id="completed"
                                            label="Completed Levels"
                                        />
                                        <RadioGroupItem
                                            value="uncompleted"
                                            id="uncompleted"
                                            label="Uncompleted Levels"
                                        />
                                    </RadioGroup>

                                    <Checkbox
                                        id={'favorited_by_user'}
                                        checked={
                                            localParams.filters
                                                .favorited_by_user
                                        }
                                        onCheckedChange={(value) =>
                                            handleFavoriteChange(!!value)
                                        }
                                        className="my-4"
                                    />
                                    <label
                                        className="ml-2 clamp-[text-sm-lg-clamp]"
                                        htmlFor={'favorited_by_user'}
                                    >
                                        Only show favorite levels
                                    </label>

                                    <h3 className="clamp-[text-sm-lg-clamp] font-semibold mb-2">
                                        NLW Tiers:
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {nlwTiers.map((tier) => (
                                            <div
                                                key={tier}
                                                className="flex items-center space-x-2"
                                            >
                                                <Checkbox
                                                    id={tier}
                                                    checked={selectedTiers.has(
                                                        tier,
                                                    )}
                                                    onCheckedChange={() =>
                                                        handleTierChange(tier)
                                                    }
                                                />
                                                <label
                                                    className="clamp-[text-sm-lg-clamp]"
                                                    htmlFor={tier}
                                                >
                                                    {tier}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {rangeProperties.map((property) => (
                                    <div
                                        key={property}
                                        className="clamp-[mb-1-4-clamp] p-2 rounded-xl"
                                    >
                                        <h3 className="clamp-[text-sm-lg-clamp] font-semibold capitalize">
                                            {propertyToLabelMap[property] ||
                                                property}
                                            :
                                        </h3>
                                        <div className="flex items-center space-x-2">
                                            <span className="clamp-[text-sm-lg-clamp] text-secondary-foreground">
                                                From
                                            </span>
                                            <NumberInput
                                                decimalPlaces={2}
                                                value={
                                                    localParams.filters.ranges[
                                                        property
                                                    ]?.min
                                                }
                                                onChange={(value) =>
                                                    handleRangeChange(
                                                        property,
                                                        'min',
                                                        value,
                                                    )
                                                }
                                                className="rounded-none hover:bg-transparent clamp-[text-sm-lg-clamp] w-18 px-2 py-1 text-center bg-transparent border-b border-secondary-light text-secondary-foreground focus:outline-none focus:border-primary"
                                            />
                                            <span className="clamp-[text-sm-lg-clamp] text-secondary-foreground">
                                                to
                                            </span>
                                            <NumberInput
                                                decimalPlaces={2}
                                                value={
                                                    localParams.filters.ranges[
                                                        property
                                                    ]?.max
                                                }
                                                onChange={(value) =>
                                                    handleRangeChange(
                                                        property,
                                                        'max',
                                                        value,
                                                    )
                                                }
                                                className="rounded-none hover:bg-transparent clamp-[text-sm-lg-clamp] w-18 px-2 py-1 text-center bg-transparent border-b border-secondary-light text-secondary-foreground focus:outline-none focus:border-primary"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </ScrollArea>
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <h3 className="clamp-[text-lg-xl-clamp] font-semibold mb-2">
                            Tags
                        </h3>
                        {
                            <ListTagsSelector
                                availableItems={
                                    (searchParams.list === 'classic'
                                        ? classicTags
                                        : platTags) as unknown as string[]
                                }
                                itemStates={localParams.filters.tags}
                                setItemStates={(tags) =>
                                    setLocalParams((prev) => ({
                                        ...prev,
                                        filters: {
                                            ...prev.filters,
                                            tags,
                                        },
                                    }))
                                }
                            />
                        }
                    </div>
                </div>
                <DialogFooter className="mb-1">
                    <Button onClick={applyFilters}>Apply</Button>
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={resetFilters}>
                        Reset all
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ListFiltersModal;
